import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { isTokenExpired } from '../utils/token';
import { queryClient } from '../query-client';

// Fallback — относительный путь: фронт и API отдаются с одного origin через reverse-proxy.
// Если фронт и бэкенд живут на разных доменах, задаётся явный VITE_API_URL.
const API_URL = import.meta.env.VITE_API_URL || '/api';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Centralized token refresh with request queue ---

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
}

function clearAuthAndRedirect() {
  queryClient.clear();
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  // Clear zustand persisted state
  localStorage.removeItem('auth-storage');
  window.location.href = '/login';
}

/**
 * Attempt to refresh tokens. Returns new access token or null on failure.
 */
async function doRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken || isTokenExpired(refreshToken, 0)) {
    return null;
  }

  try {
    const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken }, { timeout: 5000 });
    const { accessToken, refreshToken: newRefreshToken } = response.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);

    return accessToken;
  } catch {
    return null;
  }
}

// Request interceptor: attach token + proactive refresh if near expiry
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip auth header for public endpoints
    const isPublicEndpoint =
      config.url?.includes('/auth/login') ||
      config.url?.includes('/auth/refresh');

    if (isPublicEndpoint) return config;

    let token = localStorage.getItem('accessToken');

    // Proactively refresh if token expires within 60s
    if (token && isTokenExpired(token)) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          token = await doRefresh();
          if (!token) {
            processQueue(new Error('Token refresh failed'), null);
            clearAuthAndRedirect();
            return Promise.reject(new Error('Token refresh failed'));
          }
          processQueue(null, token);
        } catch (err) {
          processQueue(err, null);
          clearAuthAndRedirect();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Another request is already refreshing, wait in queue
        token = await new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handle unexpected 401 (e.g. token revoked server-side)
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Skip retry for auth endpoints
    if (
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/refresh')
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const newToken = await doRefresh();
        if (!newToken) {
          clearAuthAndRedirect();
          return Promise.reject(error);
        }
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Wait for ongoing refresh, then retry
    return new Promise((resolve, reject) => {
      failedQueue.push({
        resolve: (token: string) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(apiClient(originalRequest));
        },
        reject,
      });
    });
  },
);

export default apiClient;
